import { forwardRef, useState } from 'react';

interface SectionProps {
  id: string;
  className?: string;
  children: React.ReactNode;
}

const Section = forwardRef<HTMLElement, SectionProps>(({ id, className = '', children }, ref) => (
  <section id={id} ref={ref} className={`py-14 md:py-20 px-4 md:px-6 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
));

Section.displayName = 'Section';

/* ───── #contact: 联系入口 1项 ───── */
export function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9kvsv8hpes2mi34ssfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试。');
      setFormStatus('error');
    }
  };

  return (
    <Section id="contact" className="bg-background-100">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        联系我们
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        有任何问题或建议？欢迎随时联系我们
      </p>
      <div className="max-w-2xl mx-auto bg-background-50 rounded-2xl border border-background-300/30 p-6 md:p-10">
        <form data-readdy-form="" onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-300 mb-2">您的称呼</label>
              <input
                type="text"
                id="contact-name"
                name="name"
                required
                className="w-full px-4 py-3 bg-background-100 border border-background-300/40 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                placeholder="请输入您的称呼"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-300 mb-2">电子邮箱</label>
              <input
                type="email"
                id="contact-email"
                name="email"
                required
                className="w-full px-4 py-3 bg-background-100 border border-background-300/40 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                placeholder="请输入您的邮箱"
              />
            </div>
          </div>
          <div>
            <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-300 mb-2">主题</label>
            <input
              type="text"
              id="contact-subject"
              name="subject"
              required
              className="w-full px-4 py-3 bg-background-100 border border-background-300/40 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
              placeholder="请输入主题"
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-300 mb-2">留言内容</label>
            <textarea
              id="contact-message"
              name="message"
              required
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 bg-background-100 border border-background-300/40 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
              placeholder="请输入您的留言（限500字）"
            ></textarea>
          </div>
          <div className="honeypot-field">
            <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
          </div>
          <button
            type="submit"
            className="w-full py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            发送留言
          </button>

          {formStatus === 'success' && (
            <p className="text-sm text-accent-400 text-center">感谢您的留言，我们会尽快回复！</p>
          )}
          {formStatus === 'error' && (
            <p className="text-sm text-primary-400 text-center">{formError}</p>
          )}
        </form>
      </div>
    </Section>
  );
}

/* ───── #cards: 图文卡片 7项 ───── */
export function CardsSection() {
  const cards = [
    {
      title: '《烈火追击》',
      tag: '动作',
      desc: '特警队长深入敌后，一场生死追击引爆银幕。国产动作片年度力作，全程高能无尿点。',
      img: 'https://readdy.ai/api/search-image?query=Intense%20action%20movie%20atmosphere%20with%20dramatic%20warm%20lighting%2C%20urban%20night%20chase%20scene%20abstract%2C%20film%20poster%20style%20with%20crimson%20and%20orange%20tones%2C%20dark%20background%2C%20cinematic%20composition%20no%20faces&width=600&height=400&seq=card-01-movie&orientation=landscape',
    },
    {
      title: '《暗夜追踪》',
      tag: '悬疑',
      desc: '连环案件背后隐藏惊天秘密，警探与罪犯的智力博弈，反转不断直到最后一秒。',
      img: 'https://readdy.ai/api/search-image?query=Dark%20mystery%20noir%20cinematic%20abstract%20scene%20with%20rain-slicked%20streets%20and%20neon%20reflections%2C%20suspenseful%20atmosphere%2C%20film%20poster%20style%20with%20deep%20blues%20and%20warm%20accents%2C%20elegant%20composition&width=600&height=400&seq=card-02-movie&orientation=landscape',
    },
    {
      title: '《孤胆英雄》',
      tag: '动作',
      desc: '一人对抗整个黑恶势力，拳拳到肉的真实格斗场面，年度最燃国产动作片。',
      img: 'https://readdy.ai/api/search-image?query=Gritty%20action%20cinematic%20abstract%20scene%20with%20dramatic%20lighting%20and%20dark%20industrial%20backdrop%2C%20heroic%20atmosphere%2C%20film%20poster%20style%20with%20warm%20crimson%20and%20gold%20accents%2C%20powerful%20composition&width=600&height=400&seq=card-03-movie&orientation=landscape',
    },
    {
      title: '《深海迷踪》',
      tag: '科幻',
      desc: '深海探秘遭遇未知生物，视觉特效震撼人心，国产科幻新标杆之作。',
      img: 'https://readdy.ai/api/search-image?query=Deep%20ocean%20mysterious%20cinematic%20abstract%20art%20with%20bioluminescent%20elements%2C%20dark%20underwater%20atmosphere%2C%20sci-fi%20film%20poster%20style%20with%20teal%20and%20dark%20tones%2C%20elegant%20minimal%20composition&width=600&height=400&seq=card-04-movie&orientation=landscape',
    },
    {
      title: '《极速营救》',
      tag: '动作',
      desc: '限时营救紧张刺激，飙车追逐场面震撼，节奏紧凑让人全程握紧拳头。',
      img: 'https://readdy.ai/api/search-image?query=High-speed%20chase%20cinematic%20abstract%20scene%20with%20motion%20blur%20and%20dramatic%20lighting%2C%20urban%20night%20atmosphere%2C%20action%20film%20poster%20style%20with%20warm%20orange%20and%20dark%20tones%2C%20dynamic%20composition&width=600&height=400&seq=card-05-movie&orientation=landscape',
    },
    {
      title: '《山野回声》',
      tag: '剧情',
      desc: '豆瓣8.5分口碑佳作，质朴山野间的人性故事，温情与力量并存的年度好片。',
      img: 'https://readdy.ai/api/search-image?query=Peaceful%20rural%20landscape%20cinematic%20abstract%20art%20with%20warm%20golden%20hour%20lighting%2C%20mountain%20village%20atmosphere%2C%20drama%20film%20poster%20style%20with%20earthy%20and%20warm%20tones%2C%20elegant%20composition&width=600&height=400&seq=card-06-movie&orientation=landscape',
    },
    {
      title: '《欢乐合伙人》',
      tag: '喜剧',
      desc: '爆笑创业故事，两个性格迥异的搭档碰撞出无限笑料，解压放松首选。',
      img: 'https://readdy.ai/api/search-image?query=Bright%20and%20cheerful%20comedy%20cinematic%20abstract%20scene%20with%20warm%20lighting%2C%20playful%20atmosphere%2C%20comedy%20film%20poster%20style%20with%20golden%20and%20warm%20tones%2C%20joyful%20composition%20on%20dark%20background&width=600&height=400&seq=card-07-movie&orientation=landscape',
    },
  ];

  return (
    <Section id="cards" className="bg-background-50">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        精选推荐
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        编辑团队精选，每周更新口碑好片
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c, i) => (
          <div
            key={i}
            className={`group bg-background-100 rounded-2xl border border-background-300/30 overflow-hidden hover:border-primary-500/20 transition-colors ${i === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`}
          >
            <div className={`relative ${i === 0 ? 'aspect-video' : 'aspect-[3/2]'} overflow-hidden`}>
              <img
                src={c.img}
                alt={c.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent"></div>
              <div className="absolute top-3 left-3">
                <span className="px-2.5 py-1 rounded-full bg-primary-500/85 text-xs font-medium text-background-50 whitespace-nowrap">
                  {c.tag}
                </span>
              </div>
              <div className="absolute bottom-3 left-3 right-3">
                <h3 className="text-sm md:text-base font-heading font-bold text-foreground-100">{c.title}</h3>
              </div>
            </div>
            <div className="p-4">
              <p className="text-xs text-foreground-500 leading-relaxed">{c.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ───── #ranking: 热度榜单 6项 ───── */
export function RankingSection() {
  const ranking = [
    { rank: 1, title: '《烈火追击》', tag: '动作', heat: '本周播放量第一', change: 'up' },
    { rank: 2, title: '《暗夜追踪》', tag: '悬疑', heat: '新上榜飙升', change: 'up' },
    { rank: 3, title: '《孤胆英雄》', tag: '动作', heat: '连续三周前十', change: 'same' },
    { rank: 4, title: '《欢乐合伙人》', tag: '喜剧', heat: '口碑持续发酵', change: 'up' },
    { rank: 5, title: '《深海迷踪》', tag: '科幻', heat: '科幻类播放第一', change: 'down' },
    { rank: 6, title: '《山野回声》', tag: '剧情', heat: '高分口碑推荐', change: 'up' },
  ];

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'bg-accent-500 text-background-50';
    if (rank === 2) return 'bg-secondary-500 text-background-50';
    if (rank === 3) return 'bg-primary-500/70 text-background-50';
    return 'bg-background-300/40 text-foreground-500';
  };

  return (
    <Section id="ranking" className="bg-background-100">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        热播排行榜
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        本周最受欢迎影片实时排名
      </p>
      <div className="max-w-3xl mx-auto bg-background-50 rounded-2xl border border-background-300/30 overflow-hidden">
        {ranking.map((r, i) => (
          <div
            key={r.rank}
            className={`flex items-center gap-4 p-4 md:p-5 ${
              i < ranking.length - 1 ? 'border-b border-background-300/20' : ''
            } hover:bg-background-100/50 transition-colors cursor-pointer`}
          >
            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${getRankColor(r.rank)}`}>
              {r.rank}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground-200 truncate">{r.title}</span>
                <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-primary-500/10 text-xs text-primary-400 border border-primary-500/20 whitespace-nowrap">
                  {r.tag}
                </span>
              </div>
              <p className="text-xs text-foreground-500 mt-1">{r.heat}</p>
            </div>
            <div className="flex-shrink-0">
              {r.change === 'up' && <i className="ri-arrow-up-fill text-accent-400 text-sm"></i>}
              {r.change === 'down' && <i className="ri-arrow-down-fill text-foreground-500 text-sm"></i>}
              {r.change === 'same' && <i className="ri-subtract-line text-foreground-500 text-sm"></i>}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}